import {
  ITEM_MAX_PRICE_CENTS,
  ITEM_MIN_PRICE_DEFAULT_CENTS,
  classifyScannedCode,
  formatLabelCode,
  isValidLabelCode,
  normalizeLabelCode,
  sumCents,
} from '@barso/shared';
import { isReduced } from './promo';
import type { CartLine, CatalogItem } from './types';

/** Preisgrenzen wie auf dem Server (Etikett und Verkauf), eine Quelle in `@barso/shared`. */
export const MIN_PRICE_CENTS = ITEM_MIN_PRICE_DEFAULT_CENTS;
export const MAX_PRICE_CENTS = ITEM_MAX_PRICE_CENTS;

export type Rejection = 'invalid_format' | 'bad_check_digit' | 'in_cart';
/** Pflicht-Rückfragen beim Scan. „Noch nicht angenommen“ gehört nicht dazu: Es ist ein Hinweis ohne Bestätigung (U17e). */
export type Warning = 'sold' | 'deleted' | 'unknown';

export type ScanOutcome =
  /**
   * `notReceived`: Der Artikel war noch nicht als angenommen erfasst. Er kommt ohne Rückfrage in den Korb (Hinweis statt
   * Bestätigung), denn er zählt seit der Klärungsregel ohnehin für den Verkäufer; der Server legt die Konfliktzeile wie bisher an.
   */
  | { kind: 'added'; line: CartLine; notReceived?: true }
  /** Die Kasse warnt; der Kassierer entscheidet („Trotzdem kassieren“). Der Server speichert solche Zeilen als Konflikt. */
  | { kind: 'warning'; reason: Warning; code: string; item: CatalogItem | null; message: string }
  | { kind: 'rejected'; reason: Rejection; message: string };

const WARNINGS: Record<Warning, string> = {
  sold: 'Dieser Artikel ist schon verkauft.',
  deleted: 'Dieser Artikel wurde vom Verkäufer gelöscht.',
  unknown: 'Dieser Code gehört zu keinem Artikel (Stand des Snapshots prüfen).',
};

/** Marke an der Zeile im Warenkorb (Anzeige „nicht angenommen“). */
export const NOT_RECEIVED_TAG = 'nicht angenommen';
/** Erklärung zu einer Zeile ohne Annahme (Blatt „Artikel“, Hinweis beim Erfassen). */
export const NOT_RECEIVED_TEXT =
  'Noch nicht als angenommen erfasst. Der Artikel zählt trotzdem für den Verkäufer; die Basar-Leitung prüft das.';

export function warningText(reason: Warning): string {
  return WARNINGS[reason];
}

const lineOf = (item: CatalogItem, override: CartLine['override']): CartLine => ({
  code: item.code,
  priceCents: item.priceCents,
  itemId: item.id,
  title: item.title,
  sellerNumber: item.sellerNumber,
  itemNo: item.itemNo ?? null,
  size: item.size,
  override,
  // Einverständnis mit der Rabattaktion (X7): nur gesetzt, wenn vorhanden (andere Zeilen bleiben unverändert)
  ...(item.discountAllowed === true ? { discountAllowed: true } : {}),
});

/**
 * Bewertet einen gescannten oder getippten Code (POS-02, POS-03): Form und Prüfziffer offline, dann der Artikel im lokalen
 * Snapshot. Tippfehler werden sofort abgewiesen. Ein Code, der schon im Warenkorb liegt, wird nicht doppelt gezählt.
 */
export function evaluateScan(
  raw: string,
  labelSalt: number,
  item: CatalogItem | undefined,
  cart: readonly CartLine[],
): ScanOutcome {
  // BARSO-Code (10 Ziffern mit Prüfziffer) oder ein vom Verkäufer übernommener fremder Code (z. B. EAN)
  const scanned = classifyScannedCode(raw);
  if (scanned === null) {
    return {
      kind: 'rejected',
      reason: 'invalid_format',
      message: 'Ein Code besteht aus zehn Ziffern (oder ist ein übernommenes Etikett).',
    };
  }
  const code = scanned.code;
  if (scanned.kind === 'label' && !isValidLabelCode(code, labelSalt)) {
    return {
      kind: 'rejected',
      reason: 'bad_check_digit',
      message: 'Die Prüfziffer stimmt nicht: Tippfehler oder Etikett eines anderen Basars.',
    };
  }
  // Derselbe Artikel über den anderen Code zählt ebenfalls als „schon im Warenkorb“
  if (cart.some((line) => line.code === code || (item !== undefined && line.itemId === item.id))) {
    return {
      kind: 'rejected',
      reason: 'in_cart',
      message: 'Dieser Artikel liegt schon im Warenkorb.',
    };
  }
  if (item === undefined) {
    return {
      kind: 'warning',
      reason: 'unknown',
      code,
      item: null,
      message:
        scanned.kind === 'external'
          ? 'Dieser Code ist keinem Artikel zugeordnet (vielleicht der Herstellercode der Ware). Bitte das BARSO-Etikett scannen.'
          : WARNINGS.unknown,
    };
  }
  if (item.deleted) {
    return { kind: 'warning', reason: 'deleted', code, item, message: WARNINGS.deleted };
  }
  if (item.status === 'sold') {
    return { kind: 'warning', reason: 'sold', code, item, message: WARNINGS.sold };
  }
  if (item.status !== 'received') {
    return { kind: 'added', line: lineOf(item, 'not_received'), notReceived: true };
  }
  return { kind: 'added', line: lineOf(item, null) };
}

/** Zeile nach der Bestätigung einer Warnung (bei unbekanntem Code mit dem vom Kassierer genannten Preis). */
export function confirmWarning(
  warning: Extract<ScanOutcome, { kind: 'warning' }>,
  manualPriceCents?: number,
): CartLine | null {
  if (warning.item === null) {
    if (
      manualPriceCents === undefined ||
      !Number.isInteger(manualPriceCents) ||
      manualPriceCents < MIN_PRICE_CENTS ||
      manualPriceCents > MAX_PRICE_CENTS
    ) {
      return null;
    }
    return {
      code: warning.code,
      priceCents: manualPriceCents,
      itemId: null,
      title: 'Unbekannter Artikel',
      sellerNumber: null,
      size: null,
      override: 'unknown',
    };
  }
  return lineOf(warning.item, warning.reason);
}

export function cartTotal(cart: readonly CartLine[]): number {
  return sumCents(cart.map((line) => line.priceCents));
}

/**
 * Ersparnis durch eine Rabattaktion (U18i) in ganzen Cent: Σ Listenpreis − Σ Preis der Zeilen mit Aktion. Nur Zeilen, deren
 * Preis wirklich unter dem Listenpreis liegt, zählen. Eine Preiskorrektur an der Kasse (U17g) ist kein Rabatt und zählt nie mit.
 * Das Ergebnis ist nie negativ.
 */
export function promoSavings(lines: readonly CartLine[]): number {
  return sumCents(
    lines.flatMap((line) =>
      line.priceChange === undefined && isReduced(line) && line.promo != null
        ? [line.promo.listPriceCents - line.priceCents]
        : [],
    ),
  );
}

/** Rückgeld zu einer gegebenen Summe (Cent); `null`, solange das Gegebene nicht reicht. */
export function changeFor(totalCents: number, givenCents: number): number | null {
  return givenCents >= totalCents ? givenCents - totalCents : null;
}

/** Schnellwahl für „Gegeben“: passende Scheine ab der Summe (5, 10, 20, 50, 100 Euro), höchstens vier Vorschläge. */
export function quickGiven(totalCents: number): number[] {
  const notes = [500, 1000, 2000, 5000, 10_000];
  const options = new Set<number>([totalCents]);
  for (const note of notes) {
    if (note >= totalCents) options.add(note);
    else options.add(Math.ceil(totalCents / note) * note);
  }
  return [...options].sort((a, b) => a - b).slice(0, 5);
}

/** Code für die Anzeige in der Liste: ein BARSO-Code gruppiert („48213 90737“), ein fremder Code unverändert. */
export function displayCode(code: string): string {
  return /^\d{10}$/.test(normalizeLabelCode(code)) ? formatLabelCode(code) : code;
}
